//DQRuleTuningAssistant.tsx
import React, { useState, useMemo } from "react";
import { Button, Modal, Spinner, Form, Badge, Accordion, Card } from "react-bootstrap";
import Select from 'react-select'; 
import DQRuleTransformationEditor from "../ddm/DQRuleTransformationEditor";
import { adriva_sendChatMsg } from "../API";
import { DQ_RULE_CAT_LABELS } from "../constants";


interface DQRuleTuningAssistantProps {
    show: boolean;
    onHide: () => void;
    dataModelData: any;
    appNm: string;
    businessContext: string;
    entity: string;
    domain: string;
    fieldObj: any;
    relatedFields?: string[];
    ruleContext?: any; 
    onRuleAccept?: (rule: any) => void;
}

const safeRender = (v: any) => {
    if (v === null || v === undefined) return "-";
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return v;
    try {
        return JSON.stringify(v, null, 2);
    } catch {
        return String(v);
    }
};


const customStyles = {
    // Style for the Group Headers (e.g., "Other Fields")
    groupHeading: (provided: any) => ({
        ...provided,
        fontWeight: 'bold', 
        fontSize: '0.95em', 
        color: '#495057',   
        marginBottom: '0.25em',
        paddingTop: '0.5em',
        paddingBottom: '0.25em',
        paddingLeft: '12px', 
        borderBottom: '1px solid #dee2e6', 
    }),
    
    // Style for individual options (e.g., field names)
    option: (provided: any, state: any) => ({
        ...provided,
        fontSize: '0.9em', 
        padding: '8px 12px',
        paddingLeft: '24px', 
        color: state.isSelected ? '#fff' : '#212529', 
        backgroundColor: state.isSelected 
            ? '#0d6efd' 
            : state.isFocused 
            ? '#e9ecef' 
            : 'white',
    }),
    // Style for the container to match Bootstrap form size better
    control: (provided: any) => ({
        ...provided,
        minHeight: '38px', 
    }),
};

const extractFieldOptions = (dm: any): any[] => {
    // Map to group options by their table/group name for the react-select GroupedOptions type
    const groups: { [key: string]: { label: string; options: any[] } } = {};

    const recurse = (node: any) => {
        // Check if the current node has a field property and is not null/undefined
        if (!node || !node.field || !Array.isArray(node.field)) return;

        for (const f of node.field) {
            if (f.eClass === "Field") {
                // Use TABLE_DISPLAY_NM, then TABLE_NM, then fallback
                const groupLabel = f.TABLE_DISPLAY_NM || f.TABLE_NM || "Other Fields";
                
                if (!groups[groupLabel]) {
                    groups[groupLabel] = {
                        label: groupLabel,
                        options: [],
                    };
                }
                const physicalColNm  = f.FLD_PHYSICAL_ID.split('-')[1] === 'None' ? f.COLUMN_NAME : f.FLD_PHYSICAL_ID.split('-')[1];
                // Use FLD_ID as the unique value and a display name for the label
                groups[groupLabel].options.push({
                    value: physicalColNm, // This should be the unique identifier (e.g., the field ID)
                    label: f.COLUMN_DISPLAY_NAME + ' ( ' + physicalColNm + ' )',
                });
            } else if (f.eClass === "FieldGroup") {
                recurse(f);
            }
        }
    };

    // Assuming the top-level dataModelData contains the structure to traverse
    if (dm && dm.field) {
        recurse(dm);
    }
    
    return Object.values(groups);
};

const DQRuleTuningAssistant: React.FC<DQRuleTuningAssistantProps> = ({
    show,
    onHide,
    dataModelData, // This is the source for fields
    appNm,
    businessContext,
    entity,
    domain,
    fieldObj,
    relatedFields = [],
    ruleContext,
    onRuleAccept
}) => {
    const [userFeedback, setUserFeedback] = useState("");
    const [loading, setLoading] = useState(false);
    const [llmResponse, setLlmResponse] = useState<any>(null);
    const [selectedRuleCategory, setSelectedRuleCategory] = useState<string>("");
    const [selectedDependencyFields, setSelectedDependencyFields] = useState<string[]>([]); // Array of FLD_IDs

    const contextType = ruleContext ? "rule" : "field";
    const titleLabel = ruleContext ? `Rule: ${ruleContext.id || ruleContext.rule_id || "?"}` : `Field: ${fieldObj?.orig_column_name}`;

   
    const fieldOptions = useMemo(() => {
        // Ensure you pass the correct part of dataModelData that contains the field structure
        return extractFieldOptions(dataModelData);
    }, [dataModelData]);


    /**
     * Robust extractor for various LLM response shapes.
     * Returns either parsed JSON (object/array) or raw string.
     */
    function extractResponseContent(resp: any): string | object {
        // ... (function body remains the same as in your prompt)
        // ... (omitted for brevity)
        let candidate: any = undefined;

        try {
            if (!resp) return "";

            // common shapes
            if (resp.data && typeof resp.data.content === "string") {
                candidate = resp.data.content;
            } else if (typeof resp.content === "string") {
                candidate = resp.content;
            } else if (resp.data && (Array.isArray(resp.data) || typeof resp.data === "object")) {
                // sometimes the API returns structured object/array directly in data
                return resp.data;
            } else if (typeof resp === "string") {
                candidate = resp;
            } else if (resp.data && typeof resp.data === "string") {
                candidate = resp.data;
            } else {
                // fallback: try to stringify top-level useful fields
                candidate = JSON.stringify(resp);
            }

            if (!candidate) return "";

            // unwrap code fences and markdown wrappers: ```json { ... } ``` or ```{...}```
            // and also remove leading/trailing backticks or language tags.
            const fencedMatch = candidate.match(/```(?:json|json5|js|typescript|ts)?\s*([\s\S]*?)\s*```/i);
            if (fencedMatch && fencedMatch[1]) {
                candidate = fencedMatch[1].trim();
            } else {
                // strip single backticks too
                candidate = candidate.replace(/^`+|`+$/g, "").trim();
            }

            // try direct JSON.parse
            try {
                return JSON.parse(candidate);
            } catch (e) {
                // Not strict JSON — attempt to locate first `{` or `[` and parse substring
                const firstBrace = candidate.indexOf("{");
                const firstBracket = candidate.indexOf("[");
                let start = -1;
                if (firstBrace === -1 && firstBracket === -1) {
                    // no JSON-looking content: return raw string
                    return candidate;
                }
                if (firstBrace === -1) start = firstBracket;
                else if (firstBracket === -1) start = firstBrace;
                else start = Math.min(firstBrace, firstBracket);

                const maybeJson = candidate.slice(start);

                // Try to fix trailing garbage by finding the matching bracket (basic)
                try {
                    return JSON.parse(maybeJson);
                } catch {
                    // as last resort return the raw (unparsed) candidate string
                    return candidate;
                }
            }
        } catch (err) {
            // never throw; return stringified fallback
            try {
                return typeof resp === "string" ? resp : JSON.stringify(resp);
            } catch {
                return String(resp);
            }
        }
    }

    const handleSubmit = async () => {
        if (contextType === "field" && !selectedRuleCategory) {
            alert("Please select a DQ Rule Category.");
            return;
        }
        if (!userFeedback.trim()) return;
        setLoading(true);
        setLlmResponse(null);

        const conditionalPayload = contextType === "field" ? {
            requested_rule_category: selectedRuleCategory,
            // ⚠️ Pass the selected FLD_IDs/Values (not the full field objects)
            dependency_fields: selectedDependencyFields, 
        } : {};

        try {
            const payload = {
                agent: "DataProfileInsightAssistant",
                call_mode: "rule_improve",
                business_context: businessContext,
                domain,
                entity,
                context_type: contextType,
                field_name: fieldObj?.orig_column_name,
                field_profile: fieldObj?.field_profile,
                related_fields: relatedFields,
                current_rule_json: ruleContext || {},
                user_feedback: userFeedback,
                entity_id: dataModelData?.entityId,
                column_name: fieldObj?.column_name,
                ...conditionalPayload
            };

            const response = await adriva_sendChatMsg(appNm, "", "111", JSON.stringify(payload));
            const extracted = extractResponseContent(response);

            // Save structured object if we got one, otherwise save raw text
            setLlmResponse(extracted);
        } catch (err: any) {
            setLlmResponse(`❌ Error: ${err?.message || String(err)}`);
        } finally {
            setLoading(false);
        }
    };



    const renderRuleCard = (rule: any, idx: number, validationResults?: any[], options: any = {}) => {
        const { showAccept = true } = options;
        const vr = validationResults?.find(
            (r) => r.rule_id === rule.id || r.rule_id === rule.rule_id
        );

        return (
            <Accordion.Item eventKey={String(idx)} key={idx}>
            <Accordion.Header>
                <div className="d-flex justify-content-between align-items-center w-100">
                <div>
                    <strong>{rule.id || rule.rule_id || `Rule ${idx + 1}`}</strong>{" "}
                    <Badge bg="info">{rule.act || "N/A"}</Badge>
                </div>
                <Badge
                    bg={
                    rule.sev === "H" ? "danger" : rule.sev === "M" ? "warning" : "success"
                    }
                >
                    {rule.sev === "H" ? "High" : rule.sev === "M" ? "Medium" : "Low"}
                </Badge>
                </div>
            </Accordion.Header>
            <Accordion.Body>

                <div className="d-flex flex-wrap gap-2 small text-muted mb-2">
                <span>
                    <strong>Category:</strong>{" "}
                    <Badge bg="secondary">{DQ_RULE_CAT_LABELS[rule.cat || ""] || rule.cat || "Uncategorized"}</Badge>
                </span>
                {rule.lifecycle && (
                    <span>
                    <strong>Status:</strong>{" "}
                    <Badge bg="secondary">{rule.lifecycle}</Badge>
                    </span>
                )}
                {rule.dq_dim && (
                    <span>
                    <strong>DQ Dim:</strong>{" "}
                    <Badge bg="secondary">{rule.dq_dim}</Badge>
                    </span>
                )}
                </div>

                <div className="mb-2">
                {rule.d && (
                    <>
                    <strong>Description:</strong>
                    <div>{rule.d}</div>
                    </>
                )}
                {rule.fix && (
                    <>
                    <strong>Correction:</strong>
                    <div>{rule.fix}</div>
                    </>
                )}
                {rule.bi && (
                    <>
                    <strong>Business Impact:</strong>
                    <div>{rule.bi}</div>
                    </>
                )}
                </div>

                {(rule.vl || rule.ve || rule.vc || rule.td || rule.ex || rule.exv) && (
                <details className="mb-2">
                    <summary style={{ cursor: "pointer", fontWeight: 600 }}>
                    🔧 Technical Details
                    </summary>
                    <div className="mt-2">
                    {rule.vl && (
                        <div>
                        <strong>Validation Logic:</strong> {rule.vl}
                        </div>
                    )}
                    {rule.td && (
                        <div>
                        <strong>Transform Detail:</strong> {rule.td}
                        </div>
                    )}

                    {rule.te && (
                    <DQRuleTransformationEditor
                        transform={rule.te}
                        onChange={(updated) =>{ rule.te = updated; onRuleAccept && onRuleAccept(rule)}}
                    />
                    )}
                    {rule.vc && (
                        <div>
                        <strong>SQL Check:</strong>
                        <pre>{rule.vc}</pre>
                        </div>
                    )}
                    {rule.ve && (
                        <div>
                        <strong>Validation Expr:</strong>
                        <pre>{JSON.stringify(rule.ve, null, 2)}</pre>
                        </div>
                    )}
                    {Array.isArray(rule.ex) && rule.ex.length > 0 && (
                        <div>
                        <strong>Examples:</strong>
                        <ul>
                            {rule.ex.map((exItem: any, j: number) => (
                            <li key={j}>
                                <em>Before:</em> {JSON.stringify(exItem.before)} →{" "}
                                <em>After:</em> {JSON.stringify(exItem.after)}{" "}
                                {exItem.note && `(${exItem.note})`}
                            </li>
                            ))}
                        </ul>
                        </div>
                    )}
                    {Array.isArray(rule.exv) && rule.exv.length > 0 && (
                        <div>
                        <strong>Example Values:</strong>
                        <ul>
                            {rule.exv.map((v: any, k: number) => (
                            <li key={k}>
                                {typeof v === "object" ? JSON.stringify(v) : String(v)}
                            </li>
                            ))}
                        </ul>
                        </div>
                    )}
                    </div>
                </details>
                )}

                {/* 🧩 Validation Results Section */}
                {vr && (
                <details className="mt-3">
                    <summary style={{ cursor: "pointer", fontWeight: 600 }}>
                    🧩 Validation Results
                    </summary>
                    <div className="mt-2">
                        <div key={idx} className="mb-3 border rounded p-2 bg-light">
                            <div><strong>Status:</strong> {vr.status}</div>
                            {vr.dimension === 'Conformity' ? (
                            <div>
                                <strong>Applied Records:</strong>{" "}
                                {vr.examples && vr.examples?.affected_count} applied  / {vr.total_records} total
                            </div>
                            ) : (
                            <div>
                                <strong>Records:</strong>{" "}
                                {vr.fail_count} failed / {vr.passed_count} passed / {vr.total_records} total
                            </div>
                            )}

                            {vr.sample_failures && vr.sample_failures?.length > 0 && (
                            <div className="mt-2">
                                <strong>Sample Failures:</strong>
                                <pre className="bg-white p-2 rounded small">
                                {JSON.stringify(vr.sample_failures, null, 2)}
                                </pre>
                            </div>
                            )}

                            {vr.examples && (
                            <div className="mt-2">
                                <strong>Examples:</strong>
                                <div className="mt-2 p-2 bg-white rounded border small">
                                {Array.isArray(vr.examples.sample_changes) && vr.examples.sample_changes.length > 0 ? (
                                    vr.examples.sample_changes.slice(0, 5).map((ch: any, i: number) => (
                                    <Card
                                        key={i}
                                        className="p-2 mt-2 bg-light"
                                        style={{ fontSize: "0.85rem" }}
                                    >
                                        {Object.entries(ch.field_changes as Record<string, { before: any; after: any }>).map(
                                        ([fname, diff]) => {
                                        const beforeVal = (diff as any)?.before ?? "";
                                        const afterVal = (diff as any)?.after ?? "";
                                        return (
                                            <div key={fname}>
                                            <b>{"Record Index:"} {ch.index}</b>:{" "}
                                            <span className="text-danger">{String(beforeVal)}</span> →{" "}
                                            <span className="text-success">{String(afterVal)}</span>
                                            </div>
                                        );
                                        })}
                
                                    </Card>
                                    ))
                                ) : (
                                    <pre style={{ whiteSpace: "pre-wrap" }}>{safeRender(vr.examples)}</pre>
                                )}
                                {Array.isArray(vr.examples.sample_changes) && vr.examples?.sample_changes?.length > 5 && (
                                    <div className="text-muted mt-1">... and {vr.examples.sample_changes.length - 5} more</div>
                                )}
                                </div>
                            </div>
                            )}
                        </div>
                    </div>
                </details>
                )}

                {/* Accept button */}
                {showAccept && (
                <div className="text-end mt-3">
                <Button
                    variant="success"
                    size="sm"
                    onClick={() => onRuleAccept && onRuleAccept(rule)}
                >
                    ✅ Accept Rule
                </Button>
                </div>
                )}
            </Accordion.Body>
            </Accordion.Item>
        );
    };


    const renderRelatedFieldsSelect = () => {
        // Filter out the current field's FLD_ID/Value from the options list
        const filteredOptions = fieldOptions.map(group => ({
            ...group,
            options: group.options.filter((opt:any) => opt.value !== fieldObj?.column_name) // Use the fieldObj's unique identifier (e.g., column_name or FLD_ID)
        })).filter(group => group.options.length > 0); // Remove groups with no remaining fields

        const handleSelectChange = (selected: any) => {
            // react-select returns an array of { value: string, label: string } objects
            // We only need the 'value' (FLD_ID) for the dependency_fields payload.
            const selectedValues = selected ? selected.map((option: any) => option.value) : [];
            setSelectedDependencyFields(selectedValues);
        };

        // Format the current selected values for the Select component
        const value = filteredOptions.flatMap(group => group.options)
            .filter(opt => selectedDependencyFields.includes(opt.value));

        return (
            <Form.Group className="mb-3">
                <Form.Label>Fields for Consistency Check (Select multiple):</Form.Label>
                <Select
                    isMulti
                    options={filteredOptions} // Use the grouped options
                    value={value}
                    onChange={handleSelectChange}
                    placeholder="Select one or more dependency fields..."
                    closeMenuOnSelect={false}
                    isDisabled={filteredOptions.length === 0}
                    styles={customStyles}
                />
            </Form.Group>
        );
    };


    // ... (rest of the component's body remains the same)
    const extractedRules = useMemo(() => {
        if (!llmResponse) return [];
        // If the response is already an array/object shaped as { data: [...] }
        if (Array.isArray(llmResponse)) {
          // maybe the API returned data array directly
          return llmResponse.flatMap((col: any) => col.validation_rules || []);
        }
        if (typeof llmResponse === "object") {
          // Prioritize common shapes:
          if (Array.isArray(llmResponse.tuned_rules)) return llmResponse.tuned_rules;
          if (Array.isArray(llmResponse.data)) {
            // data may be [{ column_name, validation_rules: [...] }, ...]
            return llmResponse.data.flatMap((col: any) => col.validation_rules || []);
          }
          // If object is already a single rule or contains validation_rules
          if (Array.isArray((llmResponse as any).validation_rules)) return (llmResponse as any).validation_rules;
          // unknown object — try to find nested validation_rules
          const nested = (function findFirstValidationRules(obj: any): any[] | null {
            if (!obj || typeof obj !== "object") return null;
            if (Array.isArray(obj.validation_rules)) return obj.validation_rules;
            for (const k of Object.keys(obj)) {
              if (typeof obj[k] === "object") {
                const f = findFirstValidationRules(obj[k]);
                if (f) return f;
              }
            }
            return null;
          })(llmResponse);
          return nested || [];
        }
        return [];
      }, [llmResponse]);

    const extractedValidationResults = useMemo(() => { 
        const root = llmResponse?.data || llmResponse;
        if (root?.length > 0) return root[0].validation_results || [];
        if (root && root.validation_results) return root.validation_results;
        if (root?.data?.validation_results) return root.data.validation_results;
        if (root?.data?.length > 0) return root.data[0].validation_results || [];
        return [];
    }, [llmResponse]);


    return (
        <Modal show={show} onHide={onHide} size="xl" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title>
                    🧠 DQ Rule Tuning Assistant <Badge bg="info">{titleLabel}</Badge>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>

                {/* Existing Rule Section (if tuning a specific rule) */}
                {ruleContext && (
                <Accordion alwaysOpen defaultActiveKey={["0"]}>
                    {renderRuleCard(ruleContext, -1, [], { showAccept: false, defaultOpen: true })}
                </Accordion>
                )}

                {contextType === "field" && (
                    <Card className="mb-3 p-3 bg-light">
                        <h4>New Rule Generation Options</h4>
                        <Form.Group className="mb-3">
                            <Form.Label>Select Desired DQ Rule Category: <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                as="select"
                                value={selectedRuleCategory}
                                onChange={(e) => {
                                    setSelectedRuleCategory(e.target.value);
                                    // Reset dependency fields if category changes away from Consistency
                                    if (e.target.value !== 'cons') {
                                        setSelectedDependencyFields([]);
                                    }
                                }}
                                required
                            >
                                <option value="">-- Select Rule Type --</option>
                                {Object.entries(DQ_RULE_CAT_LABELS).map(([key, label]) => (
                                    <option key={key} value={key}>{label}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>

                        {/* Consistency Rules require multi-select field dependency */}
                        {selectedRuleCategory === 'cons' && renderRelatedFieldsSelect()}
                    </Card>
                )}

                <Form.Group className="mb-3">
                    <Form.Label>Describe what you want to improve or tune:</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={userFeedback}
                        onChange={(e) => setUserFeedback(e.target.value)}
                        placeholder="e.g., Relax this rule for nulls, or suggest better thresholds..."
                    />
                </Form.Group>

                <div className="text-end mb-3">
                    <Button variant="primary" onClick={handleSubmit} disabled={loading}>
                        {loading ? <Spinner animation="border" size="sm" /> : "Submit"}
                    </Button>
                </div>

                {/* Render structured rules if present */}
                {loading ? null : extractedRules.length > 0 ? (
                    <Accordion alwaysOpen>{extractedRules.map((r:any, i:number) => renderRuleCard(r, i, extractedValidationResults, { showAccept: true }))}</Accordion>
                ) : llmResponse ? (
                    <div className="bg-light p-3 rounded" style={{ whiteSpace: "pre-wrap" }}>
                        {typeof llmResponse === "string" ? llmResponse : JSON.stringify(llmResponse, null, 2)}
                    </div>
                ) : null}
            </Modal.Body>
        </Modal>
    );
};

export default DQRuleTuningAssistant;